// "PIN Security" panel that lives inside the profile settings.
// Lets the agent create or rotate the 6-digit PIN that gates SSN reveals
// (and, in the future, payment-method / bank-account reveals).
//
// Visibility rules:
//   - Agents (AGENCY_OWNER / AGENT / SOLO_AGENT) → can set/rotate.
//   - SEAT users → see a read-only message: PIN belongs to their
//     supervising agent, contact them to change it.
//   - PLATFORM_OWNER → no panel (handled by the parent — this component
//     assumes it's only mounted for users who manage a CRM).
//
// Forgot-PIN reset is intentionally out of scope (Phase 2 — needs
// an email round-trip with a one-time token).

import { useEffect, useState } from 'react';
import { Lock, ShieldCheck, RotateCw, KeyRound, Mail } from 'lucide-react';
import { getPinStatus, setPin, requestPinReset, resetPin } from '../api.js';

const SEAT_ROLES = new Set(['assistant']);

export function PinSecurityPanel({ currentUser, lang }) {
  const es = lang === 'es';
  const role = currentUser?.role;
  const isSeat = SEAT_ROLES.has(role);

  const [status, setStatus] = useState(null); // { hasPin, ownsPin } | null while loading
  const [loadErr, setLoadErr] = useState(null);
  const [form, setForm] = useState({ currentPin: '', newPin: '', confirmPin: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);

  // Forgot-PIN flow state. Two stages:
  //   stage 'request'  → user clicks "Forgot PIN?" → we POST
  //                       /auth/request-pin-reset → backend emails a
  //                       6-digit code. We display the masked address.
  //   stage 'consume'  → user types the code + new PIN → we POST
  //                       /auth/reset-pin → success closes the modal.
  // null = closed.
  const [resetStage, setResetStage] = useState(null);
  const [resetSentTo, setResetSentTo] = useState(null);
  const [resetForm, setResetForm] = useState({ code: '', newPin: '', confirmPin: '' });
  const [resetBusy, setResetBusy] = useState(false);
  const [resetErr, setResetErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoadErr(null);
    getPinStatus()
      .then((s) => { if (!cancelled) setStatus(s); })
      .catch((e) => { if (!cancelled) setLoadErr(e.message); });
    return () => { cancelled = true; };
  }, []);

  const fieldStyle = {
    width: '100%',
    background: 'var(--c-surface2,#112538)',
    border: '1px solid var(--c-border,#1a3550)',
    borderRadius: 8,
    padding: '10px 14px',
    color: 'var(--c-white,#eef6ff)',
    fontSize: '1rem',
    fontFamily: 'var(--font-mono)',
    letterSpacing: '.3em',
    outline: 'none',
    boxSizing: 'border-box',
    textAlign: 'center',
  };
  const labelStyle = { fontSize: '.7rem', color: 'var(--c-text3,#45688a)', display: 'block', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em' };

  const onlyDigits = (v) => String(v || '').replace(/\D/g, '').slice(0, 6);

  const submit = async (e) => {
    e?.preventDefault();
    setErr(null);
    if (form.newPin.length !== 6) {
      setErr(es ? 'El PIN debe tener exactamente 6 dígitos' : 'PIN must be exactly 6 digits');
      return;
    }
    if (form.newPin !== form.confirmPin) {
      setErr(es ? 'Los PINs no coinciden' : 'PINs do not match');
      return;
    }
    if (status?.hasPin && form.currentPin.length !== 6) {
      setErr(es ? 'Ingresa tu PIN actual' : 'Enter your current PIN');
      return;
    }
    setSaving(true);
    try {
      await setPin({ newPin: form.newPin, currentPin: status?.hasPin ? form.currentPin : undefined });
      setSaved(true);
      setForm({ currentPin: '', newPin: '', confirmPin: '' });
      setStatus({ hasPin: true, ownsPin: true });
      setTimeout(() => setSaved(false), 3000);
    } catch (e2) {
      if (e2.code === 'CURRENT_PIN_WRONG') {
        setErr(es ? 'PIN actual incorrecto' : 'Current PIN is incorrect');
      } else {
        setErr(e2.message);
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Loading / error ───────────────────────────────────────────────────────
  if (loadErr) {
    return (
      <div style={{ background: 'var(--c-red-dim)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 9, padding: '.75rem 1rem', color: 'var(--c-red,#ff3d6b)', fontSize: '.85rem' }}>
        {es ? 'Error al cargar:' : 'Failed to load:'} {loadErr}
      </div>
    );
  }
  if (!status) {
    return <div style={{ color: 'var(--c-text3,#45688a)', fontSize: '.85rem' }}>{es ? 'Cargando...' : 'Loading...'}</div>;
  }

  // ── Seat: read-only ───────────────────────────────────────────────────────
  if (isSeat) {
    return (
      <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '.75rem' }}>
          <Lock size={16} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
          <div style={{ fontSize: '.95rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>{es ? 'PIN de seguridad' : 'Security PIN'}</div>
        </div>
        <div style={{ fontSize: '.85rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.6 }}>
          {es
            ? 'Como asiento, usas el PIN de tu agente supervisor cuando necesitas ver SSN, tarjetas o cuentas. Pídeselo a tu agente. Si necesita cambiar el PIN, debe hacerlo desde su propio perfil.'
            : 'As a seat, you use your supervising agent\'s PIN to reveal SSNs, cards, or bank accounts. Ask your agent for the PIN. They must change it from their own profile.'}
        </div>
        <div style={{ marginTop: '.75rem', fontSize: '.75rem', color: status.hasPin ? 'var(--c-green,#00ff88)' : 'var(--c-amber,#ffb700)', display: 'flex', alignItems: 'center', gap: 6 }}>
          {status.hasPin ? <ShieldCheck size={14} /> : <KeyRound size={14} />}
          {status.hasPin
            ? (es ? 'Tu agente ya tiene PIN configurado.' : 'Your agent has a PIN configured.')
            : (es ? 'Tu agente NO tiene PIN configurado todavía.' : 'Your agent has not configured a PIN yet.')}
        </div>
      </div>
    );
  }

  // ── Agent: full form ──────────────────────────────────────────────────────
  return (
    <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '1.5rem', maxWidth: 480 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '.5rem' }}>
        <Lock size={16} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
        <div style={{ fontSize: '.95rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>
          {status.hasPin
            ? (es ? 'Cambiar PIN de seguridad' : 'Change Security PIN')
            : (es ? 'Crear PIN de seguridad' : 'Create Security PIN')}
        </div>
      </div>
      <div style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', marginBottom: '1.25rem', lineHeight: 1.5 }}>
        {es
          ? 'Tu PIN de 6 dígitos protege información sensible (SSN, tarjetas, cuentas bancarias). También lo usan tus asientos cuando necesitan revelar esos datos.'
          : 'Your 6-digit PIN protects sensitive data (SSN, cards, bank accounts). Your seats also use it when they need to reveal that data.'}
      </div>

      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
        {status.hasPin && (
          <div>
            <label style={labelStyle}>{es ? 'PIN actual' : 'Current PIN'}</label>
            <input
              type="text" inputMode="numeric" autoComplete="off" maxLength={6} data-1p-ignore data-lpignore="true"
              value={form.currentPin}
              onChange={(e) => setForm((f) => ({ ...f, currentPin: onlyDigits(e.target.value) }))}
              style={{ ...fieldStyle, WebkitTextSecurity: 'disc' }} placeholder="••••••"
            />
          </div>
        )}
        <div>
          <label style={labelStyle}>{es ? 'PIN nuevo (6 dígitos)' : 'New PIN (6 digits)'}</label>
          <input
            type="text" inputMode="numeric" autoComplete="off" maxLength={6} data-1p-ignore data-lpignore="true"
            value={form.newPin}
            onChange={(e) => setForm((f) => ({ ...f, newPin: onlyDigits(e.target.value) }))}
            style={{ ...fieldStyle, WebkitTextSecurity: 'disc' }} placeholder="••••••"
          />
        </div>
        <div>
          <label style={labelStyle}>{es ? 'Confirmar PIN nuevo' : 'Confirm new PIN'}</label>
          <input
            type="text" inputMode="numeric" autoComplete="off" maxLength={6} data-1p-ignore data-lpignore="true"
            value={form.confirmPin}
            onChange={(e) => setForm((f) => ({ ...f, confirmPin: onlyDigits(e.target.value) }))}
            style={{ ...fieldStyle, WebkitTextSecurity: 'disc' }} placeholder="••••••"
          />
        </div>

        {err && (
          <div style={{ fontSize: '.78rem', color: 'var(--c-red,#ff3d6b)', background: 'var(--c-red-dim)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 7, padding: '.55rem .8rem' }}>{err}</div>
        )}
        {saved && (
          <div style={{ fontSize: '.78rem', color: 'var(--c-green,#00ff88)', background: 'var(--c-green-dim)', border: '1px solid rgba(0,255,136,.25)', borderRadius: 7, padding: '.55rem .8rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <ShieldCheck size={14} />
            {es ? 'PIN guardado correctamente' : 'PIN saved successfully'}
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          style={{
            background: 'var(--c-cyan,#00d4ff)', border: 'none', borderRadius: 9, padding: '.7rem 1rem',
            color: '#0a1628', fontWeight: 800, fontSize: '.88rem', cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: saving ? 0.6 : 1, marginTop: '.4rem',
          }}
        >
          {saving ? (es ? 'Guardando...' : 'Saving...') : status.hasPin
            ? (<><RotateCw size={14} /> {es ? 'Cambiar PIN' : 'Change PIN'}</>)
            : (<><KeyRound size={14} /> {es ? 'Crear PIN' : 'Create PIN'}</>)}
        </button>

        {status.hasPin && (
          <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginTop: '.4rem', textAlign: 'center' }}>
            <button
              type="button"
              onClick={async () => {
                setResetErr(null);
                setResetBusy(true);
                setResetStage('request');
                try {
                  const data = await requestPinReset();
                  setResetSentTo(data?.sentTo || null);
                  setResetStage('consume');
                } catch (e) {
                  setResetErr(e.message);
                  setResetStage(null);
                }
                setResetBusy(false);
              }}
              style={{ background: 'transparent', border: 'none', color: 'var(--c-cyan,#00d4ff)', cursor: 'pointer', fontSize: '.75rem', padding: 0, textDecoration: 'underline' }}
            >
              {es ? '¿Olvidaste tu PIN?' : 'Forgot your PIN?'}
            </button>
          </div>
        )}
      </form>

      {resetStage && (
        <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget && !resetBusy) { setResetStage(null); setResetForm({ code: '', newPin: '', confirmPin: '' }); } }}>
          <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.5rem' }}>
              <Mail size={16} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
              <div style={{ fontWeight: 700, color: 'var(--c-white,#eef6ff)', fontSize: '.95rem' }}>
                {es ? 'Reiniciar PIN por email' : 'Reset PIN by email'}
              </div>
            </div>
            {resetStage === 'request' && (
              <div style={{ fontSize: '.82rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.5 }}>
                {es ? 'Enviando código…' : 'Sending code…'}
              </div>
            )}
            {resetStage === 'consume' && (
              <>
                <div style={{ fontSize: '.82rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.55, marginBottom: '.85rem' }}>
                  {es
                    ? <>Mandamos un código de 6 dígitos a <strong style={{ color: 'var(--c-white,#eef6ff)' }}>{resetSentTo}</strong>. Tienes <strong>15 minutos</strong>. Pégalo abajo junto con el PIN nuevo.</>
                    : <>We sent a 6-digit code to <strong style={{ color: 'var(--c-white,#eef6ff)' }}>{resetSentTo}</strong>. You have <strong>15 minutes</strong>. Paste it below with the new PIN.</>}
                </div>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setResetErr(null);
                  if (resetForm.code.length !== 6) { setResetErr(es ? 'El código debe tener 6 dígitos' : 'Code must be 6 digits'); return; }
                  if (resetForm.newPin.length !== 6) { setResetErr(es ? 'El PIN debe tener 6 dígitos' : 'New PIN must be 6 digits'); return; }
                  if (resetForm.newPin !== resetForm.confirmPin) { setResetErr(es ? 'Los PINs no coinciden' : 'PINs do not match'); return; }
                  setResetBusy(true);
                  try {
                    await resetPin({ code: resetForm.code, newPin: resetForm.newPin });
                    setResetStage(null);
                    setResetForm({ code: '', newPin: '', confirmPin: '' });
                    setSaved(true);
                    setTimeout(() => setSaved(false), 3000);
                  } catch (e2) {
                    if (e2.code === 'CODE_WRONG') setResetErr(es ? 'Código incorrecto' : 'Wrong code');
                    else if (e2.code === 'CODE_EXPIRED') setResetErr(es ? 'El código expiró. Pide otro.' : 'Code expired. Request another.');
                    else if (e2.code === 'NO_RESET_REQUEST') setResetErr(es ? 'No hay reset en curso. Pide un código nuevo.' : 'No reset in progress. Request a new code.');
                    else setResetErr(e2.message);
                  }
                  setResetBusy(false);
                }} style={{ display: 'flex', flexDirection: 'column', gap: '.65rem' }}>
                  <div>
                    <label style={labelStyle}>{es ? 'Código del email' : 'Code from email'}</label>
                    <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                      value={resetForm.code}
                      onChange={(e) => setResetForm((f) => ({ ...f, code: onlyDigits(e.target.value) }))}
                      style={fieldStyle} placeholder="123456" />
                  </div>
                  <div>
                    <label style={labelStyle}>{es ? 'PIN nuevo (6 dígitos)' : 'New PIN (6 digits)'}</label>
                    <input type="text" inputMode="numeric" autoComplete="off" maxLength={6} data-1p-ignore data-lpignore="true"
                      value={resetForm.newPin}
                      onChange={(e) => setResetForm((f) => ({ ...f, newPin: onlyDigits(e.target.value) }))}
                      style={{ ...fieldStyle, WebkitTextSecurity: 'disc' }} placeholder="••••••" />
                  </div>
                  <div>
                    <label style={labelStyle}>{es ? 'Confirmar PIN nuevo' : 'Confirm new PIN'}</label>
                    <input type="text" inputMode="numeric" autoComplete="off" maxLength={6} data-1p-ignore data-lpignore="true"
                      value={resetForm.confirmPin}
                      onChange={(e) => setResetForm((f) => ({ ...f, confirmPin: onlyDigits(e.target.value) }))}
                      style={{ ...fieldStyle, WebkitTextSecurity: 'disc' }} placeholder="••••••" />
                  </div>
                  {resetErr && <div style={{ fontSize: '.75rem', color: 'var(--c-red,#ff3d6b)', background: 'var(--c-red-dim)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 7, padding: '.4rem .6rem' }}>{resetErr}</div>}
                  <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
                    <button type="button" disabled={resetBusy} onClick={() => { setResetStage(null); setResetForm({ code: '', newPin: '', confirmPin: '' }); }}
                      style={{ background: 'transparent', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', borderRadius: 8, padding: '.45rem .9rem', fontSize: '.78rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                      {es ? 'Cancelar' : 'Cancel'}
                    </button>
                    <button type="submit" disabled={resetBusy}
                      style={{ background: 'var(--c-cyan,#00d4ff)', border: 'none', borderRadius: 8, padding: '.45rem 1rem', color: '#0a1628', fontWeight: 800, fontSize: '.82rem', cursor: resetBusy ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                      {resetBusy ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Actualizar PIN' : 'Update PIN')}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const overlayStyle = {
  position: 'fixed', inset: 0,
  background: 'rgba(7,13,20,0.85)',
  backdropFilter: 'blur(6px)',
  zIndex: 9999,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '1rem',
};
const dialogStyle = {
  maxWidth: 420, width: '100%',
  background: 'var(--c-bg2,#0b1622)',
  border: '1px solid var(--c-border2,#1f3f60)',
  borderRadius: 14,
  padding: '1.5rem 1.4rem',
  color: 'var(--c-text,#c8dff0)',
};
